import { useMemo } from "react";
import type { CurrentResponse, MatchRow } from "@shared/api";
import { aggregateMatches } from "@shared/lib/matchAggregate";
import { ChartState } from "@features/charts/ChartState";

const PACE_WINDOW_DAYS = 30;
const KD_TARGET = 5.0;

interface PaceSummary {
  windowDays: number;
  rows: number;
  matches: number;
  activeSeconds: number;
  kills: number;
  deaths: number;
  recentKd: number;
  killsPerDay: number;
  deathsPerDay: number;
  matchesPerDay: number;
  hoursPerDay: number;
}

interface KdProjection {
  target: number;
  currentKd: number;
  recentKd: number;
  feasible: boolean;
  additionalDeaths: number;
  additionalKills: number;
  additionalMatches: number;
  daysNeeded: number | null;
  etaDate: Date | null;
  reason: string;
}

interface MilestoneProjection {
  label: string;
  current: number;
  target: number;
  remaining: number;
  daysNeeded: number | null;
  etaDate: Date | null;
  formatValue: (value: number) => string;
}

export function Projections({ current, rows }: { current: CurrentResponse | null; rows: MatchRow[] | null }) {
  const pace = useMemo(() => (rows ? buildPace(rows) : null), [rows]);
  const kd = useMemo(() => (current && pace ? buildKdProjection(current, pace, KD_TARGET) : null), [current, pace]);
  const milestones = useMemo(() => (current && pace ? buildMilestones(current, pace) : []), [current, pace]);

  return (
    <section className="projections-section">
      <div className="section-heading">
        <h2>Pace & projections</h2>
        {pace && <span className="records-sub">{pace.matches.toLocaleString()} matches in last {pace.windowDays}d</span>}
      </div>
      {!current || !pace || !kd ? (
        <ChartState>Loading projections…</ChartState>
      ) : pace.matches === 0 ? (
        <ChartState>{`No matches in the last ${PACE_WINDOW_DAYS} days — projections need recent pace.`}</ChartState>
      ) : (
        <>
          <KdProjectionCard projection={kd} pace={pace} />
          <div className="record-grid">
            {milestones.map((m) => (
              <MilestoneCard key={m.label} milestone={m} pace={pace} />
            ))}
          </div>
          <p className="projection-note">
            Pace derived from the last {pace.windowDays} days: {pace.killsPerDay.toFixed(1)} kills/day,
            {" "}{pace.matchesPerDay.toFixed(1)} matches/day at {pace.recentKd.toFixed(2)} K/D.
          </p>
        </>
      )}
    </section>
  );
}

function KdProjectionCard({ projection, pace }: { projection: KdProjection; pace: PaceSummary }) {
  const tone = projection.feasible ? "good" : "bad";
  const gap = KD_TARGET - projection.recentKd;
  const displayEta = projection.etaDate ? formatDate(projection.etaDate) : null;

  return (
    <article className={`projection-headline tone-${tone}`}>
      <div className="projection-headline-top">
        <div>
          <span className="projection-eyebrow">Lifetime K/D target</span>
          <h3>{KD_TARGET.toFixed(2)}</h3>
        </div>
        <div className="projection-current">
          <span>Current {projection.currentKd.toFixed(3)}</span>
          <span>Recent {projection.recentKd.toFixed(2)}</span>
        </div>
      </div>
      {projection.feasible ? (
        <>
          <p className="projection-detail">
            At your recent K/D of {projection.recentKd.toFixed(2)} you need roughly{" "}
            <strong>{formatCompact(projection.additionalKills)}</strong> more kills and{" "}
            <strong>{formatCompact(projection.additionalMatches)}</strong> more matches
            (about <strong>{formatCompact(projection.additionalDeaths)}</strong> deaths).
          </p>
          <p className="projection-eta">
            At {pace.matchesPerDay.toFixed(1)} matches/day,
            {" "}ETA <strong>{projection.daysNeeded === null ? "—" : formatDays(projection.daysNeeded)}</strong>
            {displayEta ? ` (${displayEta})` : ""}.
          </p>
        </>
      ) : (
        <p className="projection-detail">
          Unreachable at current pace: recent K/D {projection.recentKd.toFixed(2)} is{" "}
          {gap > 0 ? `${gap.toFixed(2)} below` : "below"} the {KD_TARGET.toFixed(2)} target.
          {" "}You&apos;d need every future match to average above {KD_TARGET.toFixed(2)}.
        </p>
      )}
    </article>
  );
}

function MilestoneCard({ milestone, pace }: { milestone: MilestoneProjection; pace: PaceSummary }) {
  const pct = milestone.target <= 0 ? 0 : Math.min(100, (milestone.current / milestone.target) * 100);
  return (
    <article className="record-card tone-accent">
      <span>{milestone.label}</span>
      <strong>{milestone.formatValue(milestone.target)}</strong>
      <p>
        {milestone.formatValue(milestone.remaining)} to go · ETA{" "}
        {milestone.daysNeeded === null
          ? "—"
          : formatDays(milestone.daysNeeded)}
        {milestone.etaDate ? ` (${formatDate(milestone.etaDate)})` : ""}
      </p>
      <div className="milestone-bar" aria-hidden="true">
        <i style={{ width: `${pct}%` }} />
      </div>
      <p className="milestone-sub">
        At {pace.matchesPerDay.toFixed(1)} matches/day · {milestone.formatValue(milestone.current)} now
      </p>
    </article>
  );
}

function buildPace(rows: MatchRow[]): PaceSummary {
  const aggregate = aggregateMatches(rows);
  const windowSeconds = PACE_WINDOW_DAYS * 86_400;
  const days = PACE_WINDOW_DAYS;
  const recentKd = aggregate.deaths === 0 ? aggregate.kills : aggregate.kills / Math.max(1, aggregate.deaths);
  return {
    windowDays: days,
    rows: aggregate.rows,
    matches: aggregate.observedMatches,
    activeSeconds: aggregate.activeSeconds,
    kills: aggregate.kills,
    deaths: aggregate.deaths,
    recentKd,
    killsPerDay: aggregate.kills / days,
    deathsPerDay: aggregate.deaths / days,
    matchesPerDay: aggregate.observedMatches / days,
    hoursPerDay: aggregate.activeSeconds / 3600 / days,
  };
}

function buildKdProjection(current: CurrentResponse, pace: PaceSummary, target: number): KdProjection {
  const kills = current.lifetime.kills;
  const deaths = current.lifetime.deaths;
  const currentKd = deaths === 0 ? kills : kills / deaths;

  if (currentKd >= target) {
    return {
      target,
      currentKd,
      recentKd: pace.recentKd,
      feasible: true,
      additionalDeaths: 0,
      additionalKills: 0,
      additionalMatches: 0,
      daysNeeded: 0,
      etaDate: new Date(),
      reason: "already_reached",
    };
  }

  if (pace.recentKd <= target + 0.001) {
    return {
      target,
      currentKd,
      recentKd: pace.recentKd,
      feasible: false,
      additionalDeaths: 0,
      additionalKills: 0,
      additionalMatches: 0,
      daysNeeded: null,
      etaDate: null,
      reason: "pace_below_target",
    };
  }

  const additionalDeaths = (target * deaths - kills) / (pace.recentKd - target);
  const additionalKills = pace.recentKd * additionalDeaths;
  const deathsPerMatch = pace.matches <= 0 ? 0 : pace.deaths / pace.matches;
  const additionalMatches = deathsPerMatch > 0 ? additionalDeaths / deathsPerMatch : 0;
  const daysNeeded = pace.killsPerDay > 0 ? additionalKills / pace.killsPerDay : null;
  const etaDate = daysNeeded !== null ? new Date(Date.now() + daysNeeded * 86_400_000) : null;

  return {
    target,
    currentKd,
    recentKd: pace.recentKd,
    feasible: true,
    additionalDeaths: Math.max(0, additionalDeaths),
    additionalKills: Math.max(0, additionalKills),
    additionalMatches: Math.max(0, additionalMatches),
    daysNeeded,
    etaDate,
    reason: "projected",
  };
}

function buildMilestones(current: CurrentResponse, pace: PaceSummary): MilestoneProjection[] {
  const kills = current.lifetime.kills;
  const matches = current.lifetime.matches_played;
  const hours = current.derived.hours_played;
  const wins = current.lifetime.wins;

  const list: MilestoneProjection[] = [];

  const killsTarget = niceNext(kills);
  list.push({
    label: "Next kill milestone",
    current: kills,
    target: killsTarget,
    remaining: killsTarget - kills,
    daysNeeded: pace.killsPerDay > 0 ? (killsTarget - kills) / pace.killsPerDay : null,
    etaDate: pace.killsPerDay > 0 ? new Date(Date.now() + ((killsTarget - kills) / pace.killsPerDay) * 86_400_000) : null,
    formatValue: (v) => Math.round(v).toLocaleString(),
  });

  const matchesTarget = niceNext(matches);
  list.push({
    label: "Next match milestone",
    current: matches,
    target: matchesTarget,
    remaining: matchesTarget - matches,
    daysNeeded: pace.matchesPerDay > 0 ? (matchesTarget - matches) / pace.matchesPerDay : null,
    etaDate: pace.matchesPerDay > 0 ? new Date(Date.now() + ((matchesTarget - matches) / pace.matchesPerDay) * 86_400_000) : null,
    formatValue: (v) => Math.round(v).toLocaleString(),
  });

  const hoursTarget = niceNext(hours);
  list.push({
    label: "Next hours milestone",
    current: hours,
    target: hoursTarget,
    remaining: hoursTarget - hours,
    daysNeeded: pace.hoursPerDay > 0 ? (hoursTarget - hours) / pace.hoursPerDay : null,
    etaDate: pace.hoursPerDay > 0 ? new Date(Date.now() + ((hoursTarget - hours) / pace.hoursPerDay) * 86_400_000) : null,
    formatValue: (v) => `${Math.round(v).toLocaleString()}h`,
  });

  const winsTarget = niceNext(wins);
  const winsPerDay = pace.matchesPerDay * ((current.derived.win_rate || 0));
  list.push({
    label: "Next wins milestone",
    current: wins,
    target: winsTarget,
    remaining: winsTarget - wins,
    daysNeeded: winsPerDay > 0 ? (winsTarget - wins) / winsPerDay : null,
    etaDate: winsPerDay > 0 ? new Date(Date.now() + ((winsTarget - wins) / winsPerDay) * 86_400_000) : null,
    formatValue: (v) => Math.round(v).toLocaleString(),
  });

  return list;
}

function niceNext(value: number): number {
  if (value <= 0) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(value)));
  const next = Math.floor(value / mag) * mag + mag;
  return next;
}

function formatCompact(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return Math.round(value).toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
}

function formatDays(days: number): string {
  if (!Number.isFinite(days) || days < 0) return "—";
  if (days < 1) return "today";
  if (days < 45) return `${Math.round(days)}d`;
  if (days < 720) return `${Math.round(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}yr`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
